import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface RecentRow {
  jobId: string;
  url: string;
  domain: string;
  composite: number;
  designMd: number;
  nng: number;
  a11y: number;
  model: string;
  cost: number;
  submittedAt: string;
}
interface RecentResponse {
  count: number;
  total: number;
  evaluations: RecentRow[];
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function tierColor(score: number) {
  if (score >= 85) return 'text-emerald-300';
  if (score >= 70) return 'text-[var(--color-primary)]';
  if (score >= 50) return 'text-amber-300';
  return 'text-rose-300';
}

export function RecentEvaluations() {
  const q = useQuery<RecentResponse>({
    queryKey: ['recent-evals'],
    queryFn: async () => {
      const r = await fetch('/api/evaluations/recent?limit=10');
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  if (!q.data || q.data.count === 0) return null;

  return (
    <Card className="mt-8">
      <CardHeader>
        <CardTitle>Recent evaluations</CardTitle>
        <CardDescription>
          {q.data.total} site{q.data.total === 1 ? '' : 's'} evaluated so far.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="divide-y divide-[var(--color-border)]">
          {q.data.evaluations.map((row) => (
            <li key={row.jobId} className="flex items-center gap-3 py-2 text-sm">
              <span className={`min-w-[3ch] font-mono font-semibold ${tierColor(row.composite)}`}>
                {row.composite}
              </span>
              <a
                href={row.url}
                target="_blank"
                rel="noreferrer"
                className="flex-1 truncate hover:text-[var(--color-primary)]"
                title={row.url}
              >
                {row.domain}
              </a>
              <Badge variant="outline" className="font-mono text-xs">{row.model}</Badge>
              <span className="hidden font-mono text-xs text-[var(--color-muted-foreground)] sm:inline">
                ${row.cost.toFixed(4)}
              </span>
              <span className="text-xs text-[var(--color-muted-foreground)]">
                {timeAgo(row.submittedAt)}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
